const BASE_URL = (process.env.REACT_APP_BACKEND_URL || "https://support.eatwithme.online/api/openai").replace(/\/$/, "");

function getErrorText(error) {
  if (!error) return "Unknown error";
  if (typeof error === "string") return error;
  return error.message || String(error);
}

async function readResponseError(res) {
  let details = "";
  try {
    details = await res.text();
  } catch (error) {
    details = getErrorText(error);
  }
  const suffix = details ? ` - ${details}` : "";
  return `HTTP ${res.status} ${res.statusText}${suffix}`;
}

async function apiFetch(path, options = {}) {
  const url = `${BASE_URL}${path}`;
  let res;
  try {
    res = await fetch(url, options);
  } catch (error) {
    throw new Error(
      `Network error calling ${url}. Check REACT_APP_BACKEND_URL, protocol (http/https), and CORS. ${getErrorText(error)}`
    );
  }

  if (!res.ok) {
    const message = await readResponseError(res);
    throw new Error(`Request failed for ${url}: ${message}`);
  }

  return res;
}

export async function createConversation() {
  const res = await apiFetch("/conversations", { method: "POST" });
  return res.json();
}

export async function getConversation(id) {
  const res = await apiFetch(`/conversations/${id}`);
  return res.json();
}

export async function sendMessage(id, text) {
  const res = await apiFetch(`/conversations/${id}/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  });
  return res.json();
}

export async function sendMessageStream(id, text, onChunk) {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  };

  try {
    const res = await apiFetch(`/conversations/${id}/messages/stream`, requestOptions);
    if (!res.body) throw new Error("No response body in stream response");

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let done = false;
    let accumulated = "";

    while (!done) {
      const { value, done: streamDone } = await reader.read();
      done = streamDone;
      if (value) {
        const chunk = decoder.decode(value, { stream: !streamDone });
        accumulated += chunk;
        if (onChunk) onChunk(chunk, accumulated);
      }
    }
    return accumulated;
  } catch (streamError) {
    // Fallback to normal message endpoint if streaming is unavailable.
    try {
      const fallback = await sendMessage(id, text);
      const replyText = fallback?.text || fallback?.message || "";
      if (onChunk) onChunk(replyText, replyText);
      return replyText;
    } catch (fallbackError) {
      throw new Error(
        `Streaming and fallback message request failed. Stream error: ${getErrorText(streamError)}. Fallback error: ${getErrorText(fallbackError)}`
      );
    }
  }
}

export async function switchToHuman(id) {
  await apiFetch(`/conversations/${id}/switch-to-human`, { method: "POST" });
}

export async function uploadKnowledge(file, title, mode = "DISTIL") {
  const form = new FormData();
  form.append("file", file);
  if (title) form.append("title", title);
  const modeParam = encodeURIComponent(mode || "DISTIL");
  const res = await apiFetch(`/knowledge/upload?mode=${modeParam}`, {
    method: "POST",
    body: form
  });
  return res.json();
}

export async function listOllamaCollections() {
  const res = await apiFetch("/knowledge/collections");
  return res.json();
}

// Backward-compatible alias.
export const downloadOllamaCollections = listOllamaCollections;

export async function deleteKnowledgeExcerpt(id) {
  if (!id) throw new Error("Missing excerpt id");
  await apiFetch(`/knowledge/excerpts/${id}`, { method: "DELETE" });
}

export async function updateKnowledgeExcerpt(id, payload) {
  if (!id) throw new Error("Missing excerpt id");
  const res = await apiFetch(`/knowledge/excerpts/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return res.json();
  }
  return null;
}